import React, { useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import ListingCard from "@/components/ListingCard";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAnnouncementListings } from "@/hooks/useAnnouncementListings";
import type { AppliedFilters } from "@/components/marketplace/BreedingValueFilters";
import { calculateDistance, filterListings, transformListings } from "@/components/auction-card/auctionCardUtils";

interface AuctionCardProps {
  auction: {
    id: string;
    title: string;
    description: string | null;
    created_at: string;
    auction_date?: string | null;
    profiles?: {
      id: string;
      first_name: string;
      last_name: string;
      business_name: string | null;
      profile_image_url?: string | null;
    } | null;
  };
  searchTerm?: string;
  selectedBreed?: string;
  selectedType?: string;
  selectedLocation?: string;
  locationCoordinates?: [number, number] | null;
  rangeKm?: number;
  locationType?: string | null;
  appliedAdvancedFilters?: AppliedFilters;
  onVisibility?: (auctionId: string, visible: boolean) => void;
}

const AuctionCard = ({
  auction,
  searchTerm = "",
  selectedBreed = "",
  selectedType = "all",
  selectedLocation = "",
  locationCoordinates = null,
  rangeKm = 50,
  locationType = null,
  appliedAdvancedFilters,
  onVisibility,
}: AuctionCardProps) => {
  const { data: listings, isLoading } = useAnnouncementListings({ announcementId: auction.id });

  const auctionMatchesSearch = useMemo(() => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return auction.title.toLowerCase().includes(term) || (auction.description?.toLowerCase().includes(term) ?? false);
  }, [searchTerm, auction.title, auction.description]);

  const filteredListings = useMemo(() => {
    if (!listings || listings.length === 0) return [];
    return filterListings(listings, {
      searchTerm,
      auctionMatchesSearch,
      selectedBreed,
      selectedType,
      selectedLocation,
      locationCoordinates,
      rangeKm,
      locationType,
      appliedAdvancedFilters,
    });
  }, [
    listings,
    searchTerm,
    selectedBreed,
    selectedType,
    selectedLocation,
    locationCoordinates,
    rangeKm,
    locationType,
    appliedAdvancedFilters,
    auctionMatchesSearch,
  ]);

  const transformedListings = useMemo(() => transformListings(filteredListings), [filteredListings]);

  const hasActiveFilters = !!(
    searchTerm ||
    selectedBreed ||
    selectedType !== "all" ||
    selectedLocation ||
    (appliedAdvancedFilters &&
      (appliedAdvancedFilters.priceMin ||
        appliedAdvancedFilters.priceMax ||
        Object.values(appliedAdvancedFilters.breedingValues || {}).some((f) => f.value !== "")))
  );

  const isVisible =
    isLoading || !hasActiveFilters || transformedListings.length > 0 || (!!searchTerm && auctionMatchesSearch);

  useEffect(() => {
    onVisibility?.(auction.id, isVisible);
  }, [auction.id, isVisible, onVisibility]);

  if (!isVisible) return null;

  const breederName =
    auction.profiles?.business_name ||
    `${auction.profiles?.first_name || ""} ${auction.profiles?.last_name || ""}`.trim() ||
    "Unknown Breeder";

  const profileImage = auction.profiles?.profile_image_url;
  const fallbackInitial = breederName.charAt(0).toUpperCase();
  const hasListings = transformedListings.length > 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        {/* Desktop: two columns */}
        <div className="hidden lg:flex lg:items-start lg:justify-between lg:gap-6">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <h2 className="text-xl font-bold text-foreground">{auction.title}</h2>
            {auction.description && <p className="text-sm text-muted-foreground line-clamp-2">{auction.description}</p>}
            {auction.auction_date &&
              (() => {
                const auctionDate = new Date(auction.auction_date + "T00:00:00");
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isFuture = auctionDate >= today;
                return (
                  <span
                    className={`flex items-center gap-1 text-sm ${isFuture ? "text-primary" : "text-muted-foreground"}`}
                  >
                    <Calendar className="w-4 h-4" />
                    {format(auctionDate, "dd MMM yyyy")}
                  </span>
                );
              })()}
          </div>
          <div className="flex flex-col items-center gap-2 shrink-0">
            <Avatar className="h-12 w-12">
              {profileImage ? <AvatarImage src={profileImage} alt={breederName} /> : null}
              <AvatarFallback className="bg-muted text-muted-foreground font-semibold">
                {fallbackInitial}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-foreground text-center max-w-[140px] truncate">
              {breederName}
            </span>
            {hasListings && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="text-white hover:text-white bg-green-700 hover:bg-green-800"
              >
                <Link to={`/announcement/${auction.id}`}>See Auction</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile: stacked */}
        <div className="flex flex-col gap-2 lg:hidden">
          <h2 className="text-xl font-bold text-foreground">{auction.title}</h2>
          {auction.description && <p className="text-sm text-muted-foreground line-clamp-2">{auction.description}</p>}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                {profileImage ? <AvatarImage src={profileImage} alt={breederName} /> : null}
                <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                  {fallbackInitial}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{breederName}</span>
            </div>
            {auction.auction_date &&
              (() => {
                const auctionDate = new Date(auction.auction_date + "T00:00:00");
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isFuture = auctionDate >= today;
                return (
                  <span className={`flex items-center gap-1 ${isFuture ? "text-primary" : "text-muted-foreground"}`}>
                    <Calendar className="w-4 h-4" />
                    {format(auctionDate, "dd MMM yyyy")}
                  </span>
                );
              })()}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : transformedListings.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            {hasActiveFilters ? "No listings match your filters." : "No listings in this auction yet."}
          </p>
        ) : (
          <Carousel opts={{ align: "start" }} className="w-full">
            <CarouselContent>
              {transformedListings.map((listing) => (
                <CarouselItem key={listing.id} className="basis-full md:basis-1/2 xl:basis-1/3">
                  <ListingCard listing={listing} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {transformedListings.length > 3 && (
              <div className="flex justify-center gap-2 mt-4">
                <CarouselPrevious className="static translate-x-0 translate-y-0" />
                <CarouselNext className="static translate-x-0 translate-y-0" />
              </div>
            )}
          </Carousel>
        )}
        {/* Mobile "See Auction" button below listings */}
        {hasListings && (
          <div className="lg:hidden mt-4">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="text-white hover:text-white w-full bg-green-700 hover:bg-green-800"
            >
              <Link to={`/announcement/${auction.id}`}>See Auction</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AuctionCard;
